import Link from "next/link";
import { ToggleButton } from "@components/atoms/ToggleButton";
import { useShowCurrencyLabel } from "@util/useShowCurrencyLabel";
import CurrencyDuration from "@components/organisms/common/CurrencyDuration";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../../styles/cssinjs/helpers/variables.js";

export default function GetProFreePlanCards({
  plans,
  toggle,
  className,
  isAnnualBillingPlan,
  isMonthlyBillingPlan,
  showCurrencyCode = true,
}) {
  const showCurrencyCodeLabel = useShowCurrencyLabel(showCurrencyCode);
  const Toggle = () => (
    <div className="billing-type-wrap lg:mb-3 md:mb-2">
      <div className="billing-toggle">
        <div className="togg-btn-wrap">
          <ToggleButton
            label={`click to toggle between ${toggle.billingType[0]} and ${toggle.billingType[1]}.`}
            announcement={{
              onState:
                "annual billing - free plan $0 per month, pro plan $5 per month, team $5 per month.",
              offState:
                "monthly billing - free plan $0 per month, pro plan $12 per month, team $9 per month.",
            }}
            toggled={isAnnualBillingPlan}
          />
        </div>
        <span
          className={`w-auto annual-billing-span ${
            isAnnualBillingPlan ? "active" : ""
          }`}
          id="annual-billing-span"
          aria-hidden="true"
        >
          {toggle.billingType[1]}
        </span>
        <figure
          className={`abs--img monthly-billing-span w-auto ${
            isMonthlyBillingPlan ? "active" : ""
          }`}
          id="dis-img-toggle"
        >
          <svg width="106" height="24">
            <use href="/sprite.svg#fifty-annual"></use>
          </svg>
        </figure>
      </div>
      <style jsx>{`
        figure {
          opacity: 0;
        }
        figure.active {
          opacity: 1;
        }
        .togg-btn-wrap {
          display: inline-block;
          vertical-align: middle;
        }
        .billing-toggle,
        .billing-type-wrap {
          position: relative;
          height: 30px !important;
        }
        .billing-type-wrap .annual-billing-span,
        .billing-type-wrap .monthly-billing-span {
          vertical-align: middle;
          display: inline-block;
          text-align: left;
          width: calc(50% - 50px);
          font-size: ${font.size.sm[4]};
          color: ${colors.primitive.grey[300]};
        }
        .annual-billing-span.active,
        .monthly-billing-span.active {
          font-weight: ${font.weight.bold};
          color: ${colors.primitive.black[100]} !important;
        }
        .billing-type-wrap span:first-child {
          text-align: right;
        }
        .abs--img {
          position: absolute;
          transition-property: opacity;
          transition-duration: 0.5s;
        }
        #dis-img-toggle {
          position: absolute;
          left: 17%;
          top: -24px;
          display: inline-block;
          opacity: 0;
        }
        #annual-dis-img-cta.abs--img,
        #annual-dis-img.abs--img,
        #annual-pro-dis-img.abs--img,
        #dis-img-toggle.abs--img,
        #monthly-dis-img-cta.abs--img,
        #monthly-dis-img.abs--img {
          opacity: 0;
        }
        #annual-dis-img-cta.active,
        #annual-dis-img.active,
        #annual-pro-dis-img.active,
        #dis-img-toggle.active,
        #monthly-dis-img-cta.active,
        #monthly-dis-img.active {
          opacity: 1;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .abs--img {
            left: 66px;
          }
          #dis-img-toggle {
            left: 20%;
          }
          .billing-toggle #dis-img-toggle {
            left: 14%;
            top: -25px;
          }
        }
      `}</style>
    </div>
  );
  const Card = ({ data, index }) => {
    const appMode = process.env.APP_MODE || "staging";
    const link = appMode
      ? data?.button?.href?.[appMode]
      : "https://signup.staging.setmore.com/signup?planId=5506";
    const classMap = {
      0: "border-brand baseline",
      1: "md:mt-3",
    };
    const cardContainerClassName = classMap[index] || "";
    const Costs = ({ data }) =>
      data.map((cost, index) => <span key={index}>{cost}</span>);

    const PlanDetails = () => {
      const hasMultiplePrices = Array.isArray(data.price);
      return (
        <>
          <div className="fx fx--cv highlight-title">
            <h2 className="highlight">{data.heading}</h2>
            {data.hasOwnProperty("headingIcon") ? (
              <figure className="emblem">
                <svg width="32" height="32" className="setmore-emblem">
                  <use href={`/sprite.svg#setmore-emblem-xl`}></use>
                </svg>
              </figure>
            ) : null}
          </div>
          <p className="plan-user text-light-grayish-blue">{data.label}</p>
          {data.hasOwnProperty("discount") ? (
            <figure
              className={`abs--img discount-img abs--img-card ${
                isAnnualBillingPlan ? "active" : ""
              }`}
            >
              <svg
                width={data.discountDimension[0]}
                height={data.discountDimension[1]}
              >
                <use href={`/sprite.svg#${data.discount}`}></use>
              </svg>
            </figure>
          ) : null}
          {data.hasOwnProperty("discountMonthly") ? (
            <figure
              className={`abs--img monthly-billing-span md:d-none abs--img-card ${
                isMonthlyBillingPlan ? "active" : ""
              }`}
              dangerouslySetInnerHTML={{ __html: data.discountMonthly }}
            />
          ) : null}
          <p className="plan-price">
            <strong
              className={`highlight ${isAnnualBillingPlan ? "counter" : ""}  ${
                hasMultiplePrices ? "price-wrapper" : ""
              }`}
            >
              ${hasMultiplePrices ? <Costs data={data.price} /> : data.price}
            </strong>{" "}
            <span
              className={`plan-duration-wrap text-light-grayish-blue ml-5 ${
                showCurrencyCodeLabel ? "has-currency-code" : ""
              }`}
            >
              <CurrencyDuration
                show={showCurrencyCodeLabel}
                duration={data.duration}
              />
            </span>
          </p>
          {toggle ? data.hasOwnProperty("hasToggle") && <Toggle /> : null}
          <style jsx>{`
            .highlight-title,
            .baseline .fx {
              align-items: baseline !important;
            }
            .fx--sb > figure {
              opacity: 1;
              visibility: visible;
            }
            figure {
              opacity: 0;
              visibility: hidden;
            }
            figure.active {
              opacity: 1;
              visibility: visible;
            }
            .highlight,
            .plan-duration-wrap {
              transition: all 0.3s ease;
            }
            .highlight.counter + .plan-duration-wrap {
              margin-left: 24px !important;
              transition: all 0.3s ease-in;
            }
            .abs--img {
              left: 96px;
              position: absolute;
              transition-property: opacity, visibility;
              transition-duration: 0.5s;
            }
            #dis-img-toggle {
              position: relative;
              left: -30%;
              top: -5px;
              display: inline-block;
              opacity: 0;
            }
            #annual-dis-img-cta.abs--img,
            #annual-dis-img.abs--img,
            #annual-pro-dis-img.abs--img,
            #dis-img-toggle.abs--img,
            #monthly-dis-img-cta.abs--img,
            #monthly-dis-img.abs--img {
              opacity: 0;
            }
            #annual-dis-img-cta.active,
            #annual-dis-img.active,
            #annual-pro-dis-img.active,
            #dis-img-toggle.active,
            #monthly-dis-img-cta.active,
            #monthly-dis-img.active {
              opacity: 1;
            }
            .billing-toggle {
              margin-left: 38%;
              margin-right: -25px;
            }
            @media (max-width: ${viewport.tablet.sm}) {
              #dis-img-toggle {
                left: 20%;
              }
              .billing-toggle #dis-img-toggle {
                left: -30%;
                top: -5px;
              }
            }
          `}</style>
        </>
      );
    };
    const Features = () => {
      const link = data?.learnMore?.href;
      return (
        <>
          <div className="mt-2 lg:mb-4 md:mb-2 plan-benefits">
            <ul
              className="ticked-bulletin mb-0 mt-0"
              aria-label={`features in ${data.heading}`}
            >
              {data.features.map((feature, index) => (
                <li
                  className="text-grey-800"
                  key={index}
                  dangerouslySetInnerHTML={{ __html: feature }}
                />
              ))}
            </ul>
          </div>
          {link ? (
            <Link
              className={`arrow-link text-brandcolor ml-1 ${
                data.features.length == 1 ? "lg:mt-5 inline-block" : ""
              } `}
              id={data.learnMore?.id}
              href={link}
            >
              {data.learnMore?.content ? (
                <svg width="20" height="20" className="arrow-right">
                  <use href={`/sprite.svg#small-arrow`}></use>
                </svg>
              ) : null}

              <span>{data.learnMore?.content}</span>
            </Link>
          ) : null}
          <style jsx>
            {`
              .arrow-right {
                margin-right: 8px;
                vertical-align: middle;
              }
              .arrow-link {
                position: absolute;
                left: 38px;
                bottom: 24px;
              }
              .arrow-link span {
                vertical-align: middle;
              }
              .ticked-bulletin li {
                margin-bottom: 0px !important;
                width: 260px !important;
              }
              .ticked-bulletin li:last-child::before {
                ${index >= 0
                  ? `
              background: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%23fff' d='M0 0h20v20H0z'/%3E%3Cpath d='M16 15H4l6-6 6 6zM16 9H4l6-6 6 6z' stroke='%23556270' stroke-width='1.1' stroke-linejoin='round'/%3E%3C/svg%3E")
                no-repeat;
              top: 5px;
              `
                  : ``}
              }
            `}
          </style>
        </>
      );
    };
    const CtaButton = () => (
      <div className="plan-cta">
        <Link
          href={link}
          className={`${data.button.variant} w-100`}
          id={data.button.id}
        >
          {data.button.content}
        </Link>
        {data.hasOwnProperty("notice") ? (
          <div className="notice fx">
            <span> {data.notice} </span>
            <svg width="21" height="20">
              <use href="/sprite.svg#smiley-emoji"></use>
            </svg>
          </div>
        ) : null}
        <style jsx>{`
          .notice {
            justify-content: center;
            align-items: center;
            gap: 8px;
            padding: 4px 16px;
            border-radius: 14px;
            background-color: ${colors.primitive.green[600]};
            margin-top: 16px;
          }
          .notice > span {
            max-width: 177px;
            font-size: ${font.size.sm[1]};
            font-weight: bold;
            line-height: ${lineHeight[4]};
            text-transform: uppercase;
            letter-spacing: 0.25px;
            color: ${colors.primitive.green[700]};
          }
        `}</style>
      </div>
    );

    return (
      <li className={`cardwrapper ${cardContainerClassName}`}>
        <PlanDetails />
        <CtaButton />
        <Features />
        <style jsx>{`
          .cardwrapper {
            width: 47%;
            max-height: 800px;
            padding: 24px 48px 40px 48px;
          }
          .border-brand {
            border: 1px solid ${colors.primitive.green.light};
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .border-brand {
              border: none;
            }
            .cardwrapper {
              min-height: 100%;
            }
            .cardwrapper.border-brand {
              border: 1px solid ${colors.primitive.green.light};
            }
          }
        `}</style>
      </li>
    );
  };
  return (
    <ul
      className={`container card--default pr-card pr-card-toggle has-three-col has-gutter-md mx-auto lg:mb-1 cards ${className}`}
      aria-label="Pricing plan comparison."
    >
      {plans.map((plan, index) => (
        <Card data={plan} key={index} index={index} />
      ))}
    </ul>
  );
}
